import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Badge,
    LoadingState,
    PageHeader,
    Text,
    Title,
} from '@v2/design-system';
import api from '@v2/lib/api';
import CdRackGrid from '@v2/features/cd-rack/CdRackGrid';
import CdSlotDetailPanel from '@v2/features/cd-rack/CdSlotDetailPanel';
import type { CdSlot } from '@v2/features/cd-rack/CdRackGrid';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type Summary = {
    total_slots: number;
    occupied_slots: number;
    free_slots: number;
    total_items: number;
    items_cliente: number;
    items_estoque: number;
    total_value_brl: number;
    occupancy_pct: number;
    overdue_items: number;
    overdue_slots: number;
    overdue_charge_brl: number;
    free_days: number;
    daily_rate_brl: number;
    unique_products?: number;
    unique_orders?: number;
    total_units?: number;
};

type Slot = {
    code: string;
    occupied: boolean;
    count: number;
    value_brl: number;
    missing: boolean;
    cliente_count: number;
    estoque_count: number;
    occupancy_kind: 'free' | 'cliente' | 'estoque' | 'mixed';
    days_label: string | null;
    overdue_count: number;
    overdue_charge_brl: number;
    overdue_max_days: number;
    fill_pct?: number;
    capacity_units?: number;
    primary_product_name?: string | null;
    primary_order_number?: string | null;
    short_code?: string | null;
    slot_status?: string;
};

type Rua = {
    code: string;
    label: string;
    grid: Slot[][];
    bay_labels: string[];
    level_labels: string[];
};

type LayoutRow = { columns: Array<{ ruas: Rua[] }> };

type OverdueItem = {
    id: number;
    shelf_code?: string;
    rua?: string;
    product_code?: string;
    product_name?: string;
    serial_number?: string;
    batch_number?: string;
    order_number?: string;
    order_id?: number;
    customer_name?: string;
    tipo: 'Cliente' | 'Estoque';
    billable_days: number;
    total_days: number;
    total_charge_brl: number;
};

type StockSearchItem = {
    id: number;
    shelf_code?: string | null;
    rua?: string | null;
    serial_number?: string | null;
    batch_number?: string | null;
    order_number?: string | null;
    order_id?: number | null;
    customer_name?: string | null;
    tipo: 'Cliente' | 'Estoque';
    is_cliente: boolean;
    days_at_cd?: number | null;
    product?: { id: number; code?: string | null; name: string; reference?: string | null } | null;
};

type StockSearchResult = { items: StockSearchItem[]; total: number; term: string };

type ApiData = {
    summary: Summary;
    ruas: Rua[];
    layout_rows: LayoutRow[];
    overdue_items_list: OverdueItem[];
    stock_cd_sem_prateleira: number;
    cd: { name: string; code?: string; address?: string } | null;
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
const num = (v: number) => new Intl.NumberFormat('pt-BR').format(v ?? 0);

function parseHighlightCodes(raw: string | null): string[] {
    if (!raw) {
        return [];
    }

    return raw
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean);
}

export default function EstoqueDashboardPage() {
    const { t: _t } = useI18n();
    const [searchParams] = useSearchParams();
    const { data, isLoading, isError, refetch } = useQuery<ApiData>({
        queryKey: ['dashboard', 'estoque'],
        queryFn: async () => (await api.get('/dashboard/estoque')).data,
    });

    const highlightCodes = useMemo(
        () => parseHighlightCodes(searchParams.get('highlight') ?? searchParams.get('slot')),
        [searchParams],
    );

    const [selectedSlotCode, setSelectedSlotCode] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const didScrollHighlight = useRef(false);
    const detailPanelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (highlightCodes.length === 0) {
            didScrollHighlight.current = false;
            return;
        }
        setSelectedSlotCode(highlightCodes[0]);
    }, [highlightCodes]);

    useEffect(() => {
        if (!data || highlightCodes.length === 0 || didScrollHighlight.current) {
            return;
        }
        const ruaCode = highlightCodes[0].charAt(0);
        const el = document.getElementById(`cd-rua-${ruaCode}`);
        if (el) {
            didScrollHighlight.current = true;
            window.setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 80);
        }
    }, [data, highlightCodes]);

    const searchQuery = useQuery<StockSearchResult>({
        queryKey: ['dashboard', 'estoque', 'search', activeSearch],
        enabled: activeSearch.length >= 2,
        queryFn: async () =>
            (await api.get('/dashboard/estoque/search', { params: { q: activeSearch } })).data,
    });

    const ruas = data?.ruas ?? [];
    const layoutRows = data?.layout_rows ?? [];
    const summary = data?.summary;
    const overdue = data?.overdue_items_list ?? [];

    function handleSearch(e: React.FormEvent) {
        e.preventDefault();
        const term = searchInput.trim();
        if (term.length >= 2) setActiveSearch(term);
    }

    function clearSearch() {
        setSearchInput('');
        setActiveSearch('');
        searchInputRef.current?.focus();
    }

    if (isLoading) {
        return <LoadingState label="Carregando mapa do CD…" />;
    }

    if (isError || !summary) {
        return (
            <div className="space-y-6">
                <PageHeader
                    title="Estoque CD"
                    breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Estoque' }]}
                />
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                    {isError ? 'Não foi possível carregar o mapa do CD.' : 'Nenhum centro de distribuição configurado.'}
                    {isError && (
                        <button type="button" onClick={() => void refetch()} className="rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold hover:bg-white/60 dark:border-rose-500/40">
                            Tentar novamente
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const hasOverdue = summary.overdue_items > 0;
    const semPrateleira = data?.stock_cd_sem_prateleira ?? 0;

    return (
        <div className="space-y-8">
            <PageHeader
                title="Dashboard Estoque — CD"
                breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Estoque CD' }]}
            >
                {data?.cd && (
                    <Text className="text-sm text-gray-500 dark:text-zinc-400">
                        {data.cd.name}
                        {data.cd.code ? ` · ${data.cd.code}` : ''}
                        {data.cd.address ? ` · ${data.cd.address}` : ''}
                    </Text>
                )}
            </PageHeader>

            {/* ── KPIs ── */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {/* Card 1: Valor em estoque */}
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                        Valor total em estoque (CD)
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-zinc-100">
                        {brl(summary.total_value_brl)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                        {num(summary.total_units ?? summary.total_items)} unidades alocadas
                    </p>
                    <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-zinc-300">
                        <span>
                            <span className="font-semibold text-sky-700">{num(summary.items_cliente)}</span> cliente
                        </span>
                        <span>
                            <span className="font-semibold text-red-700">{num(summary.items_estoque)}</span> estoque
                        </span>
                    </p>
                </div>

                {/* Card 2: Locais */}
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                        Locais (prateleiras)
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-zinc-100">
                        {num(summary.total_slots)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                        <span className="text-emerald-600">{summary.free_slots} livres</span>
                        {' · '}
                        <span className="text-red-600">{summary.occupied_slots} ocupados</span>
                    </p>
                </div>

                {/* Card 3: Ocupação */}
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                        Ocupação
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-zinc-100">
                        {summary.occupancy_pct.toFixed(1)}%
                    </p>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
                        <div
                            className="h-full rounded-full bg-amber-500 transition-all"
                            style={{ width: `${Math.min(100, summary.occupancy_pct)}%` }}
                        />
                    </div>
                </div>

                {/* Card 4: Centro / política */}
                {data?.cd ? (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">Centro</p>
                        <p className="mt-1 text-lg font-bold text-gray-900 dark:text-zinc-100">{data.cd.name}</p>
                        {data.cd.code && <p className="text-xs text-gray-500 dark:text-zinc-400">{data.cd.code}</p>}
                        <p className="mt-2 text-[11px] text-gray-500 dark:text-zinc-400">
                            Política: <strong>{summary.free_days ?? 30}d grátis</strong>
                            {' · '}R$ {(summary.daily_rate_brl ?? 0).toFixed(2).replace('.', ',')} / m³ · dia
                        </p>
                    </div>
                ) : (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                            Armazenagem cobrável
                        </p>
                        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-zinc-100">
                            {brl(summary.overdue_charge_brl)}
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                            {summary.overdue_items} item(ns) · {summary.free_days}d grátis
                        </p>
                    </div>
                )}
            </div>

            {/* ── Alerta de armazenagem cobrável ── */}
            {hasOverdue && (
                <div className="rounded-xl border-2 border-purple-400 bg-purple-50/70 p-4 shadow-sm">
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                Itens em armazenagem cobrável
                            </p>
                            <p className="mt-1 text-2xl font-bold tabular-nums text-purple-900 dark:text-purple-200">
                                {num(summary.overdue_items)}
                            </p>
                            <p className="mt-1 text-[11px] text-purple-800/80">
                                em {summary.overdue_slots} prateleira(s) — acima de {summary.free_days} dias
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                Valor de cobrança acumulado
                            </p>
                            <p className="mt-1 text-2xl font-bold tabular-nums text-purple-900 dark:text-purple-200">
                                {brl(summary.overdue_charge_brl)}
                            </p>
                            <p className="mt-1 text-[11px] text-purple-800/80">
                                Σ (dias cobráveis × R$/m³/dia × CBM) por item
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                                Fórmula
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-purple-900 dark:text-purple-200">
                                <code className="rounded bg-white/80 px-1 py-0.5">
                                    cobrança = max(0, dias_na_posição − {summary.free_days}) × R${' '}
                                    {(summary.daily_rate_brl ?? 0).toFixed(2).replace('.', ',')} × CBM_produto
                                </code>
                            </p>
                            <p className="mt-1 text-[11px] text-purple-800/80">
                                Produto sem CBM cadastrado entra zerado. Política configurável no CD.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Aviso sem prateleira ── */}
            {semPrateleira > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:bg-amber-500/10">
                    <strong>{num(semPrateleira)} unidade(s)</strong> com localização «CD» mas{' '}
                    <strong>sem prateleira</strong> (
                    <code className="rounded bg-white/90 px-1">cd_location_id</code> vazio). O mapa só conta itens com
                    endereço de prateleira — atribua no cadastro do item.
                </div>
            )}

            {/* ── Legenda ── */}
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 dark:border-zinc-600 dark:bg-zinc-800/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-300">Legenda</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {/* Mesmas cores de cd-slot-styles (slotCardClassesSolid) — antes a legenda dizia o contrário do mapa. */}
                    <SolidBadge color="#10b981" label="Livre" />
                    <SolidBadge color="#9ca3af" label="Ocupado (estoque)" />
                    <SolidBadge color="#0ea5e9" label="Ocupado (cliente)" />
                    <SolidBadge color="#8b5cf6" label="Misto" />
                    <SolidBadge color="#ef4444" label={`Em atraso (${summary.free_days ?? 30}d+)`} />
                    <SolidBadge color="#d1d5db" textColor="#374151" label="Não cadastrado" />
                </div>
                <p className="mt-3 text-xs text-gray-500 dark:text-zinc-400">
                    Clique numa célula para ver as unidades. Os dias na posição contam a partir da entrada no CD.
                </p>
            </div>

            {/* ── Busca no CD ── */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-zinc-900 dark:border-zinc-700">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">Busca no CD</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                    Pedido, código ou nome do produto, serial, cliente ou ID de pedido/embarque.
                </p>
                <form onSubmit={handleSearch} className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="min-w-[min(100%,18rem)] flex-1">
                        <input
                            ref={searchInputRef}
                            type="search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Ex.: PED-DEMO, código produto, serial…"
                            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-950 shadow-sm placeholder:text-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 dark:bg-zinc-900 dark:border-zinc-600"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={searchInput.trim().length < 2}
                        className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
                    >
                        Buscar
                    </button>
                    {activeSearch && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="text-sm font-medium text-gray-600 underline hover:text-gray-900 dark:text-zinc-300"
                        >
                            Limpar
                        </button>
                    )}
                </form>

                {searchQuery.isFetching && (
                    <p className="mt-3 text-xs text-gray-400">Buscando…</p>
                )}

                {activeSearch.length >= 2 && searchQuery.data && (
                    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-zinc-800">
                        {searchQuery.data.total === 0 ? (
                            <p className="text-sm text-amber-800">
                                Nenhuma unidade encontrada com «{searchQuery.data.term}».
                            </p>
                        ) : (
                            <>
                                <p className="mb-3 text-sm font-medium text-gray-900 dark:text-zinc-100">
                                    {searchQuery.data.total} unidade(s) — «{searchQuery.data.term}»
                                </p>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
                                        <thead className="bg-gray-50 dark:bg-zinc-800/60">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Rua</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Prateleira</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Produto</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Serial / Lote</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Pedido</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Cliente</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Tipo</th>
                                                <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-zinc-300">Dias</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white dark:bg-zinc-900 dark:divide-zinc-800">
                                            {searchQuery.data.items.map((it) => (
                                                <tr key={it.id} className="hover:bg-gray-50">
                                                    <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900 dark:text-zinc-100">
                                                        {it.rua ? `RUA ${it.rua}` : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-700 dark:text-zinc-300">
                                                        {it.shelf_code ?? '—'}
                                                    </td>
                                                    <td className="max-w-[14rem] px-3 py-2 text-gray-800 dark:text-zinc-100">
                                                        <div className="font-medium">{it.product?.name ?? '—'}</div>
                                                        {it.product?.code && (
                                                            <div className="truncate text-xs text-gray-500 dark:text-zinc-400">
                                                                {it.product.code}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-gray-600 dark:text-zinc-300">
                                                        {it.serial_number ?? '—'}
                                                        {it.batch_number && (
                                                            <span className="text-xs"> · {it.batch_number}</span>
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-zinc-300">
                                                        {it.order_number ?? '—'}
                                                        {it.order_id && (
                                                            <span className="text-xs text-gray-500 dark:text-zinc-400"> #{it.order_id}</span>
                                                        )}
                                                    </td>
                                                    <td className="max-w-[10rem] truncate px-3 py-2 text-gray-600 dark:text-zinc-300">
                                                        {it.customer_name ?? '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2">
                                                        <span
                                                            className={cn(
                                                                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white',
                                                                it.is_cliente ? 'bg-sky-600' : 'bg-red-500',
                                                            )}
                                                        >
                                                            {it.tipo}
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700 dark:text-zinc-300">
                                                        {it.days_at_cd ?? '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* ── Mapa do CD ── */}
            {ruas.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
                    <p className="font-semibold">Nenhuma localização do CD encontrada.</p>
                    <p className="mt-2 text-sm">
                        Cadastre as ruas e prateleiras do CD em{' '}
                        <Link to="/settings/distribution-centers" className="font-semibold underline">Centros de distribuição</Link>.
                    </p>
                </div>
            ) : (
                <div className="space-y-8">
                    {(layoutRows.length > 0
                        ? layoutRows
                        : [{ columns: ruas.map((r) => ({ ruas: [r] })) }]
                    ).map((row, ri) => (
                        <div
                            key={ri}
                            className={cn(
                                'grid gap-6',
                                row.columns.length === 1
                                    ? 'grid-cols-1'
                                    : row.columns.length === 2
                                      ? 'sm:grid-cols-2'
                                      : 'sm:grid-cols-2 lg:grid-cols-3',
                            )}
                        >
                            {row.columns.map((col, ci) =>
                                col.ruas.map((rua) => (
                                    <section
                                        key={`${ri}-${ci}-${rua.code}`}
                                        id={`cd-rua-${rua.code}`}
                                        className={cn(
                                            'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm lg:p-5',
                                            highlightCodes.some((code) => code.startsWith(rua.code)) &&
                                                'ring-2 ring-amber-300/80 dark:ring-[#C9A84C]/50',
                                        )}
                                    >
                                        <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">{rua.label}</h2>
                                                <p className="text-xs text-gray-500 dark:text-zinc-400">
                                                    6 colunas · 3 andares (topo → base)
                                                </p>
                                            </div>
                                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 dark:text-zinc-300">
                                                Rua {rua.code}
                                            </span>
                                        </div>
                                        <CdRackGrid
                                            rua={rua}
                                            variant="compact"
                                            solidColors
                                            highlightCodes={highlightCodes}
                                            selectedCode={selectedSlotCode}
                                            onSlotClick={(slot: CdSlot) => {
                                                setSelectedSlotCode(slot.code);
                                                // O painel fica depois de todas as ruas: leva a tela até ele.
                                                window.requestAnimationFrame(() =>
                                                    detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                                                );
                                            }}
                                        />
                                    </section>
                                )),
                            )}
                        </div>
                    ))}

                    {/* Painel de detalhe do slot selecionado */}
                    {selectedSlotCode && (
                        <div ref={detailPanelRef} className="scroll-mt-24">
                        <CdSlotDetailPanel
                            slotCode={selectedSlotCode}
                            onClose={() => setSelectedSlotCode(null)}
                            warehouseSummary={{
                                total_slots: summary.total_slots,
                                occupied_slots: summary.occupied_slots,
                                free_slots: summary.free_slots,
                                ruas_count: ruas.length,
                            }}
                        />
                        </div>
                    )}
                </div>
            )}

            {/* ── Armazenagem em cobrança ── */}
            {overdue.length > 0 && (
                <section className="rounded-xl border border-purple-300 bg-white p-4 shadow-sm dark:bg-zinc-900">
                    <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">Armazenagem em cobrança</h3>
                            <p className="text-xs text-gray-500 dark:text-zinc-400">
                                Unidades acima de {summary.free_days} dias na posição. Política do CD:{' '}
                                <strong>
                                    R$ {(summary.daily_rate_brl ?? 0).toFixed(2).replace('.', ',')} / m³ · dia
                                </strong>{' '}
                                após o período grátis.
                            </p>
                        </div>
                        <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-900 dark:text-purple-200">
                            {overdue.length} item(ns) · {brl(summary.overdue_charge_brl)}
                        </span>
                    </header>

                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
                        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
                            <thead className="bg-purple-50 dark:bg-purple-500/10">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Prateleira</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Produto</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Cliente / Pedido</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-zinc-300">Tipo</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-zinc-300">Dias cobr.</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-zinc-300">Total dias</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-zinc-300">Cobrança</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white dark:bg-zinc-900 dark:divide-zinc-800">
                                {overdue.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-700 dark:text-zinc-300">
                                            {row.shelf_code ?? '—'}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-gray-900 dark:text-zinc-100">{row.product_name ?? '—'}</div>
                                            {row.product_code && (
                                                <div className="text-xs text-gray-500 dark:text-zinc-400">{row.product_code}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-gray-600 dark:text-zinc-300">
                                            {row.customer_name ?? row.order_number ?? '—'}
                                            {row.order_number && row.customer_name && (
                                                <div className="text-xs text-gray-500 dark:text-zinc-400">Ped. {row.order_number}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={cn(
                                                    'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white',
                                                    row.tipo === 'Cliente' ? 'bg-sky-600' : 'bg-red-500',
                                                )}
                                            >
                                                {row.tipo}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-semibold text-purple-800">
                                            {row.billable_days}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-600 dark:text-zinc-300">
                                            {row.total_days}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium text-rose-700">
                                            {brl(row.total_charge_brl)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}

function SolidBadge({
    color,
    textColor = '#fff',
    label,
}: {
    color: string;
    textColor?: string;
    label: string;
}) {
    return (
        <span
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm"
            style={{ backgroundColor: color, color: textColor }}
        >
            {label}
        </span>
    );
}
